// "+ Action" / "+ Waiting": a short form over the Case for the next step:
// its title, the kind, who is waited on (a person, picked or created), a due
// date and what to do.
//
// Promise: Enter in the title or ⌘/Ctrl+Enter anywhere adds; a waiting whose
// person is still being created cannot be added yet. The form closes when the
// step is sent; if the server refuses it, the caller opens the form again with
// what was written (`initial`) and the reason (`error`).
import { CASE_TEXT_MAX, CASE_TITLE_MAX } from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import { useRef, useState } from "react";
import { useTimeZone } from "../me/profile";
import { PersonPill, PILL, type PickedPerson } from "../persons/person-pick";
import { Choices, ErrorLine } from "../ui/bits";
import { Button } from "../ui/button";
import { Icon } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { Modal } from "../ui/modal";
import { DayPopover } from "./bits";
import { endOfDay, formatMonthDay } from "./dates";
import type { StepAddInput } from "./queries";

export type StepKind = "action" | "waiting";

export interface StepDraft {
  kind: StepKind;
  title: string;
  body: string;
  /** YYYY-MM-DD or "". */
  due: string;
  person: PickedPerson | null;
}

export const emptyDraft = (kind: StepKind): StepDraft => ({
  kind,
  title: "",
  body: "",
  due: "",
  person: null,
});

export function AddStepModal({
  initial,
  error,
  blocked,
  onClose,
  onSubmit,
}: {
  initial: StepDraft;
  /** Why the last add was refused, shown under the form. */
  error: string | null;
  /** Why nothing can be added yet (a new Case without a title), or null. */
  blocked: string | null;
  onClose(): void;
  /** Sends the step; false when it was not sent (the form stays). */
  onSubmit(step: StepAddInput, draft: StepDraft): boolean;
}) {
  const timeZone = useTimeZone();
  const [kind, setKind] = useState<StepKind>(initial.kind);
  const [title, setTitle] = useState(initial.title);
  const [body, setBody] = useState(initial.body);
  const [due, setDue] = useState(initial.due);
  const [person, setPerson] = useState<PickedPerson | null>(initial.person);
  const [personError, setPersonError] = useState<string | null>(null);
  const [dueOpen, setDueOpen] = useState(false);
  const dueAnchor = useRef<HTMLButtonElement>(null);
  const waitOn = kind === "waiting" ? person : null;
  const canAdd = blocked === null && title.trim() !== "" && waitOn?.id !== null;

  const add = () => {
    if (!canAdd) return;
    const draft: StepDraft = {
      kind,
      title: title.trim(),
      body: body.trim(),
      due,
      person: waitOn,
    };
    const at = draft.due ? endOfDay(draft.due, timeZone) : undefined;
    const sent = onSubmit(
      {
        kind: draft.kind,
        title: draft.title,
        ...(draft.body ? { body: draft.body } : {}),
        ...(at
          ? draft.kind === "waiting"
            ? { wait_until: at }
            : { due_at: at }
          : {}),
        ...(draft.person?.id
          ? { wait_kind: "person", wait_id: draft.person.id }
          : {}),
      },
      draft,
    );
    if (sent) onClose();
  };

  const label =
    kind === "waiting" ? m.cases_add_waiting() : m.cases_add_action();
  return (
    <Modal
      open
      onClose={onClose}
      label={label}
      className="!w-[min(560px,calc(100vw-32px))]"
    >
      {/* ⌘/Ctrl+Enter from any field of the form adds. */}
      <div
        onKeyDownCapture={(e) => {
          if (
            e.key === "Enter" &&
            (e.metaKey || e.ctrlKey) &&
            !isImeComposing(e.nativeEvent)
          ) {
            e.preventDefault();
            e.stopPropagation();
            add();
          }
        }}
      >
        <input
          // biome-ignore lint/a11y/noAutofocus: the form was opened to write this
          autoFocus
          value={title}
          maxLength={CASE_TITLE_MAX}
          aria-label={
            kind === "waiting"
              ? m.cases_waiting_title()
              : m.cases_action_title()
          }
          placeholder={
            kind === "waiting"
              ? m.cases_waiting_title_placeholder()
              : m.cases_action_title_placeholder()
          }
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isImeComposing(e.nativeEvent)) {
              e.preventDefault();
              add();
            }
          }}
          className="plain-field w-full border-none bg-transparent p-0 text-[18px] text-fg outline-none"
        />
        <div className="mt-3 flex flex-wrap items-center gap-[6px]">
          <Choices<StepKind>
            label={m.cases_step_kind()}
            value={kind}
            options={[
              { value: "action", label: m.cases_kind_action() },
              { value: "waiting", label: m.cases_kind_waiting() },
            ]}
            onChange={setKind}
          />
          {kind === "waiting" && (
            <PersonPill
              value={person}
              onChange={setPerson}
              onError={setPersonError}
            />
          )}
          <button
            ref={dueAnchor}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={dueOpen}
            onClick={() => setDueOpen((v) => !v)}
            className={`${PILL} ${due ? "" : "text-muted"}`}
          >
            <Icon kind="calendar" size={13} />
            {due
              ? m.cases_step_due({ date: formatMonthDay(due) })
              : m.cases_due()}
          </button>
          <DayPopover
            anchor={dueAnchor}
            open={dueOpen}
            onClose={() => setDueOpen(false)}
            label={m.cases_due()}
            value={due}
            onChange={(d) => setDue(d ?? "")}
            clearLabel={m.cases_due_clear()}
          />
        </div>
        {kind === "waiting" && personError && (
          <ErrorLine>{personError}</ErrorLine>
        )}
        <textarea
          value={body}
          maxLength={CASE_TEXT_MAX}
          aria-label={m.cases_body()}
          placeholder={m.cases_body_write_placeholder()}
          onChange={(e) => setBody(e.target.value)}
          className="plain-field mt-4 block min-h-[96px] w-full resize-none border-none bg-transparent p-0 text-[13.5px] leading-[1.7] text-body outline-none [field-sizing:content]"
        />
        {(blocked ?? error) && <ErrorLine>{blocked ?? error}</ErrorLine>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {m.common_cancel()}
          </Button>
          <Button variant="primary" disabled={!canAdd} onClick={add}>
            {label}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
